import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Briefcase, UserPlus, Kanban, ChartBar, Sparkle, Plus, Star,
  Envelope, VideoCamera, Phone, DownloadSimple, ThumbsUp, ThumbsDown,
  Copy, Users, ChartLineUp, Clock,
} from "@phosphor-icons/react";

const RECO_TONE = {
  strong_yes: "bg-emerald-100 border-emerald-300 text-emerald-800",
  yes: "bg-emerald-50 border-emerald-200 text-emerald-700",
  no: "bg-red-50 border-red-200 text-red-700",
  strong_no: "bg-red-100 border-red-300 text-red-800",
};

function AIParseCard({ candidate, jobId, onParsed }) {
  const [busy, setBusy] = useState(false);
  const parsed = candidate?.ai_parsed;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ats/ai/parse-resume", { candidate_id: candidate.id, job_id: jobId });
      toast.success(`Parsed — fit score ${data.fit_score || "n/a"}`);
      onParsed();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  if (!parsed) {
    return (
      <Button size="sm" onClick={run} disabled={busy} data-testid={`ai-parse-${candidate.id}`} className="rounded-sm bg-zenith-magenta hover:bg-zenith-magenta/90 h-7 text-xs">
        <Sparkle size={12} weight="fill" className="mr-1" /> {busy ? "Parsing…" : "AI parse resume"}
      </Button>
    );
  }
  return (
    <div className="border border-zenith-magenta/30 bg-zenith-magenta/5 rounded-sm p-2 text-xs">
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={12} weight="fill" className="text-zenith-magenta" />
        <div className="font-semibold">AI fit: {parsed.fit_score ?? "—"}/100</div>
      </div>
      {parsed.years_exp !== undefined && <div>{parsed.years_exp} yrs · {parsed.current_designation || parsed.current_company || ""}</div>}
      {parsed.top_skills?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{parsed.top_skills.slice(0, 6).map(s => <Badge key={s} variant="outline" className="rounded-sm text-[9px]">{s}</Badge>)}</div>}
      {parsed.fit_summary && <div className="text-[10px] text-zinc-600 mt-1 italic line-clamp-3">{parsed.fit_summary}</div>}
    </div>
  );
}


function ApplicationDetailDialog({ application, stages, users, onChanged }) {
  const [open, setOpen] = useState(false);
  const [scorecards, setScorecards] = useState([]);
  const [scForm, setScForm] = useState({ stage: application.stage, overall: 3, recommendation: "yes", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/ats/applications/${application.id}/scorecards`); setScorecards(data); }
    catch { /* ignore */ }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const move = async (stage) => {
    try { await api.post(`/ats/applications/${application.id}/move`, { stage }); toast.success(`Moved to ${stage}`); onChanged(); setOpen(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitScorecard = async () => {
    setBusy(true);
    try {
      await api.post(`/ats/applications/${application.id}/scorecard`, scForm);
      toast.success("Scorecard submitted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`app-open-${application.id}`} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 rounded-sm">Open</button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users size={16} weight="duotone" className="text-zenith-teal" />
            {application.candidate?.name} · <span className="text-zenith-magenta">{application.job_title}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-1">Candidate</div>
            <div className="text-sm"><Envelope size={12} weight="duotone" className="inline mr-1" />{application.candidate?.email}</div>
            {application.candidate?.phone && <div className="text-sm"><Phone size={12} weight="duotone" className="inline mr-1" />{application.candidate.phone}</div>}
            <div className="text-[11px] text-zinc-500 mt-1">Source: {application.source} · Applied: {application.applied_at?.slice(0, 10)}</div>
            {application.cover_letter && <div className="mt-2 text-xs italic border-l-2 border-zenith-teal pl-2">"{application.cover_letter}"</div>}
            <div className="mt-3">
              <AIParseCard candidate={application.candidate} jobId={application.job_id} onParsed={onChanged} />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-1">Move to stage</div>
            <div className="flex flex-wrap gap-1">
              {stages.map((s) => (
                <button key={s} onClick={() => move(s)} data-testid={`move-${s.replace(/\s+/g,'-').toLowerCase()}`}
                        className={`text-xs px-2 py-1 border rounded-sm ${application.stage === s ? "border-zenith-magenta bg-zenith-magenta/10 text-zenith-magenta font-bold" : "border-zinc-300 hover:bg-zinc-50"}`}>
                  {s}
                </button>
              ))}
            </div>
            {(application.stage_history || []).length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-1">History</div>
                <div className="text-[11px] space-y-1 max-h-32 overflow-y-auto">
                  {application.stage_history.map((h, i) => (
                    <div key={i}><span className="font-mono-plex text-zinc-400">{h.at?.slice(5, 16)}</span> · {h.from} → <span className="font-semibold">{h.to}</span></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-zinc-100 pt-3">
          <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">Interview scorecards</div>
          <div className="grid grid-cols-4 gap-2 mb-2">
            <Select value={scForm.stage} onValueChange={v => setScForm({ ...scForm, stage: v })}>
              <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" min="1" max="5" value={scForm.overall} onChange={e => setScForm({ ...scForm, overall: parseInt(e.target.value) || 3 })} className="rounded-sm h-8 text-xs" placeholder="Overall 1-5" data-testid="sc-overall" />
            <Select value={scForm.recommendation} onValueChange={v => setScForm({ ...scForm, recommendation: v })}>
              <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="strong_yes">Strong yes</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="strong_no">Strong no</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="sc-submit" onClick={submitScorecard} disabled={busy} className="rounded-sm h-8 text-xs bg-adb-navy hover:bg-adb-navy-800">Submit</Button>
          </div>
          <Textarea rows={2} placeholder="Notes…" value={scForm.notes} onChange={e => setScForm({ ...scForm, notes: e.target.value })} className="rounded-sm mb-2" />
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {scorecards.map(sc => (
              <div key={sc.id} className={`border rounded-sm p-2 text-xs ${RECO_TONE[sc.recommendation] || ""}`}>
                <div className="flex justify-between">
                  <span className="font-semibold">{sc.interviewer_name || "Interviewer"}</span>
                  <span>{sc.stage} · overall {sc.overall}/5 · {sc.recommendation}</span>
                </div>
                {sc.notes && <div className="mt-1 italic">"{sc.notes}"</div>}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function OfferDialog({ application, onSent }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ctc_lpa: "", joining_date: "", designation: "", location: "", letter_body: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ats/offers", { application_id: application.id, ...form, ctc_lpa: parseFloat(form.ctc_lpa) || 0 });
      setResult(data); toast.success("Offer generated"); onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const closeAll = () => { setOpen(false); setResult(null); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`offer-${application.id}`} className="rounded-sm bg-zenith-green text-adb-navy hover:bg-zenith-green/90 h-7 text-xs">
          <Envelope size={12} className="mr-1" /> Roll offer
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Envelope size={16} weight="duotone" className="text-zenith-green" /> Roll out offer</DialogTitle></DialogHeader>
        {!result ? (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Designation *</Label>
              <Input data-testid="offer-designation" value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Location</Label>
              <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label className="text-xs">CTC (LPA) *</Label>
              <Input data-testid="offer-ctc" type="number" value={form.ctc_lpa} onChange={e => setForm({ ...form, ctc_lpa: e.target.value })} className="rounded-sm mt-1 font-mono-plex" /></div>
            <div><Label className="text-xs">Joining date *</Label>
              <Input data-testid="offer-doj" type="date" value={form.joining_date} onChange={e => setForm({ ...form, joining_date: e.target.value })} className="rounded-sm mt-1 font-mono-plex" /></div>
            <div className="col-span-2"><Label className="text-xs">Letter body</Label>
              <Textarea rows={3} value={form.letter_body} onChange={e => setForm({ ...form, letter_body: e.target.value })} className="rounded-sm mt-1" placeholder="Optional custom letter body…" /></div>
          </div>
        ) : (
          <div data-testid="offer-result" className="rounded-sm border border-adb-green/40 bg-adb-green/10 p-4 space-y-2">
            <div className="font-semibold text-adb-navy">Offer generated and sent to candidate</div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/ats/offers/${result.id}/pdf`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-xs border border-adb-navy text-adb-navy px-2 py-1 rounded-sm hover:bg-adb-navy hover:text-white">
              <DownloadSimple size={12} /> Download PDF
            </a>
            <div className="text-xs">Acceptance URL (share with candidate):</div>
            <div className="flex items-center gap-2">
              <code className="text-[10px] bg-white border border-zinc-200 rounded-sm px-1 py-0.5 flex-1 truncate">
                {window.location.origin}/careers/accept-offer/{result.sign_token}
              </code>
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/careers/accept-offer/${result.sign_token}`); toast.success("Copied"); }}
                      className="p-1 rounded-sm border border-zinc-200 hover:bg-zinc-50"><Copy size={12} /></button>
            </div>
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={closeAll} className="rounded-sm">Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="offer-submit" className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">{busy ? "Sending…" : "Send offer"}</Button>
            </>
          ) : <Button onClick={closeAll} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function NewJobDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", department: "", location: "", openings: 1, description: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const genJD = async () => {
    if (!form.title) { toast.error("Enter a title first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai-gen/jd", { title: form.title, department: form.department, brief: form.description });
      const body = [
        data.summary,
        "",
        "Key responsibilities:",
        ...(data.responsibilities || []).map(r => "• " + r),
        "",
        "Must-have:",
        ...(data.requirements_must || []).map(r => "• " + r),
        "",
        "Nice-to-have:",
        ...(data.requirements_good_to_have || []).map(r => "• " + r),
      ].join("\n");
      setForm({ ...form, description: body });
      toast.success("JD drafted");
    } catch (e) { toast.error(formatApiError(e)); }
    setAiBusy(false);
  };
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post("/jobs", { ...form, openings: parseInt(form.openings) || 1 });
      toast.success("Job created"); setOpen(false); onCreated();
      setForm({ title: "", department: "", location: "", openings: 1, description: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="job-new-btn" className="rounded-sm bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold">
          <Plus size={14} className="mr-1" /> New job
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Open a new position</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Title *</Label>
            <Input data-testid="job-form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Department</Label>
            <Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Location</Label>
            <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Openings</Label>
            <Input type="number" value={form.openings} onChange={e => setForm({ ...form, openings: e.target.value })} className="rounded-sm mt-1 font-mono-plex" /></div>
          <div className="col-span-2"><Label className="text-xs flex items-center justify-between">Description
            <Button size="sm" variant="outline" onClick={genJD} disabled={aiBusy} data-testid="job-form-ai" className="rounded-sm h-6 text-[10px]"><Sparkle size={10} weight="fill" className="text-zenith-magenta mr-1" />{aiBusy ? "…" : "AI draft"}</Button>
          </Label>
            <Textarea rows={6} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="job-form-submit" onClick={submit} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function ATSPipeline() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stages, setStages] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [source, setSource] = useState([]);
  const [tth, setTth] = useState(null);
  const [jobFilter, setJobFilter] = useState("");

  const load = async () => {
    try {
      const [j, a, s, f, sr, t] = await Promise.all([
        api.get("/jobs").catch(() => ({ data: [] })),
        api.get("/ats/applications").catch(() => ({ data: [] })),
        api.get("/ats/stages").catch(() => ({ data: [] })),
        api.get("/ats/reports/funnel").catch(() => ({ data: null })),
        api.get("/ats/reports/source").catch(() => ({ data: [] })),
        api.get("/ats/reports/time-to-hire").catch(() => ({ data: null })),
      ]);
      setJobs(j.data); setApplications(a.data); setStages(s.data);
      setFunnel(f.data); setSource(sr.data); setTth(t.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filteredApps = useMemo(() => {
    if (!jobFilter) return applications;
    return applications.filter(a => a.job_id === jobFilter);
  }, [applications, jobFilter]);

  const byStage = useMemo(() => {
    const map = {};
    stages.forEach(s => { map[s] = []; });
    filteredApps.forEach(a => { if (map[a.stage] !== undefined) map[a.stage].push(a); });
    return map;
  }, [filteredApps, stages]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-magenta font-bold">Applicant Tracking</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Hire the best. Faster.</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Public careers page, AI-drafted JDs, AI resume parsing with fit scoring, interview scorecards, offer letters with e-signature. Hired candidates auto-flow into HR onboarding.</p>
        </div>
        <div className="flex gap-2">
          <a href="/careers/" target="_blank" rel="noreferrer" data-testid="view-careers" className="inline-flex items-center gap-1 border border-zinc-300 rounded-sm px-3 py-2 text-xs hover:bg-zinc-50">
            <Briefcase size={12} /> View careers page
          </a>
          <NewJobDialog onCreated={load} />
        </div>
      </div>

      <Tabs defaultValue="pipeline">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="pipeline" className="rounded-sm" data-testid="tab-ats-pipeline"><Kanban size={12} className="mr-1" /> Pipeline</TabsTrigger>
          <TabsTrigger value="jobs" className="rounded-sm" data-testid="tab-ats-jobs"><Briefcase size={12} className="mr-1" /> Jobs</TabsTrigger>
          <TabsTrigger value="interviews" className="rounded-sm" data-testid="tab-ats-interviews"><VideoCamera size={12} className="mr-1" /> My interviews</TabsTrigger>
          <TabsTrigger value="reports" className="rounded-sm" data-testid="tab-ats-reports"><ChartBar size={12} className="mr-1" /> Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Filter job:</Label>
            <Select value={jobFilter || "__all"} onValueChange={v => setJobFilter(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-64 rounded-sm h-8 text-sm"><SelectValue placeholder="All jobs" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All jobs</SelectItem>
                {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-2">
            {stages.map(s => (
              <div key={s} data-testid={`ats-col-${s.replace(/\s+/g,'-').toLowerCase()}`} className="border border-zinc-200 rounded-sm bg-zinc-50 min-h-[240px]">
                <div className="px-2 py-2 border-b border-zinc-200 font-display font-bold text-xs flex items-center justify-between bg-white rounded-t-sm">
                  <span className="truncate">{s}</span>
                  <Badge variant="outline" className="rounded-sm text-[10px]">{(byStage[s] || []).length}</Badge>
                </div>
                <div className="p-1 space-y-1 max-h-[560px] overflow-y-auto">
                  {(byStage[s] || []).map(app => (
                    <div key={app.id} data-testid={`app-card-${app.id}`} className="border border-zinc-200 rounded-sm bg-white p-2">
                      <div className="text-xs font-semibold text-adb-navy truncate">{app.candidate?.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{app.job_title}</div>
                      <div className="text-[10px] text-zinc-400 mt-1">{app.source} · {app.applied_at?.slice(0, 10)}</div>
                      {app.candidate?.ai_parsed?.fit_score !== undefined && (
                        <div className="text-[10px] font-bold text-zenith-magenta mt-1">Fit: {app.candidate.ai_parsed.fit_score}/100</div>
                      )}
                      <div className="mt-1 flex gap-1">
                        <ApplicationDetailDialog application={app} stages={stages} onChanged={load} />
                        {app.stage === "Interview - HR" && <OfferDialog application={app} onSent={load} />}
                      </div>
                    </div>
                  ))}
                  {(byStage[s] || []).length === 0 && <div className="text-[10px] text-zinc-400 text-center py-3">—</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="jobs" className="mt-4">
          <div className="overflow-x-auto border border-zinc-200 rounded-sm bg-white">
            <table className="hr-table">
              <thead><tr><th>Title</th><th>Department</th><th>Location</th><th>Openings</th><th>Status</th><th>Candidates</th></tr></thead>
              <tbody>
                {jobs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-sm">No jobs yet.</td></tr>}
                {jobs.map(j => (
                  <tr key={j.id} data-testid={`job-row-${j.id}`}>
                    <td className="font-semibold text-adb-navy">{j.title}</td>
                    <td className="text-xs">{j.department || "—"}</td>
                    <td className="text-xs">{j.location || "—"}</td>
                    <td className="font-mono-plex text-xs">{j.openings}</td>
                    <td><Badge variant="outline" className="rounded-sm">{j.status}</Badge></td>
                    <td className="font-mono-plex text-xs">{j.candidate_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="interviews" className="mt-4">
          <MyInterviewsInline />
        </TabsContent>

        <TabsContent value="reports" className="mt-4 space-y-4">
          {funnel && (
            <div className="border border-zinc-200 rounded-sm bg-white p-4">
              <div className="font-display font-bold text-adb-navy mb-2">Funnel</div>
              <div className="grid grid-cols-7 gap-1">
                {(funnel.stages || []).map(s => (
                  <div key={s} className="text-center">
                    <div className="text-[10px] text-zinc-500 uppercase truncate">{s}</div>
                    <div className="font-display text-2xl font-bold text-adb-navy">{funnel.counts[s] || 0}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="border border-zinc-200 rounded-sm bg-white p-4">
              <div className="font-display font-bold text-adb-navy mb-2">Source effectiveness</div>
              {source.length === 0 ? <div className="text-xs text-zinc-500">No applications yet.</div> :
                <table className="hr-table text-xs w-full">
                  <thead><tr><th>Source</th><th>Applied</th><th>Hired</th><th>Conv</th></tr></thead>
                  <tbody>
                    {source.map(r => (
                      <tr key={r.source}><td>{r.source}</td><td className="font-mono-plex">{r.count}</td><td className="font-mono-plex">{r.hired}</td><td className="font-mono-plex">{r.count > 0 ? Math.round((r.hired / r.count) * 100) : 0}%</td></tr>
                    ))}
                  </tbody>
                </table>
              }
            </div>
            <div className="border border-zinc-200 rounded-sm bg-white p-4">
              <div className="font-display font-bold text-adb-navy mb-2">Time to hire</div>
              {tth ? (
                <div className="grid grid-cols-3 gap-2">
                  <div><div className="text-[10px] uppercase text-zinc-500">Avg days</div><div className="font-display text-2xl">{tth.avg_days}</div></div>
                  <div><div className="text-[10px] uppercase text-zinc-500">Min</div><div className="font-display text-2xl">{tth.min_days}</div></div>
                  <div><div className="text-[10px] uppercase text-zinc-500">Max</div><div className="font-display text-2xl">{tth.max_days}</div></div>
                </div>
              ) : <div className="text-xs text-zinc-500">No hires yet.</div>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}


function MyInterviewsInline() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/ats/interviews/mine").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  if (items.length === 0) return <div className="text-xs text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-6 text-center">No interviews scheduled for you.</div>;
  return (
    <div className="space-y-2">
      {items.map(iv => (
        <div key={iv.id} className="border border-zinc-200 rounded-sm bg-white p-3 flex items-center gap-3">
          <VideoCamera size={20} weight="duotone" className="text-zenith-teal" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-adb-navy">{iv.candidate_name} · {iv.stage}</div>
            <div className="text-[11px] text-zinc-500">{iv.scheduled_at?.slice(0, 16)} · {iv.mode} · {iv.duration_min}m</div>
          </div>
          {iv.meeting_link && <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 border border-adb-navy text-adb-navy rounded-sm hover:bg-adb-navy hover:text-white">Join</a>}
        </div>
      ))}
    </div>
  );
}
